import { AppService } from '../App.service';
import { InitialConfiguration } from '../interfaces';
import { ComponentClick, EventData, FinancialProductTracking, GDL } from './interfaces/tracking-data.interface';
import { TrackingEvents } from './tracking-manager.constants';

declare global {
    interface Window {
        GDL: GDL;
    }
}

const PORSCHE_BRAND = 'Porsche';
const SUBJECT_FOR_ALARM = 'Error in tracking of data for Porsche events';
const TRACK_METHOD_NAME = 'track';

/**
 * Class to track all widget operation
 * @returns {void}
 */
class TrackingManager {
    private appService = new AppService();
    private config: InitialConfiguration;
    private financialProduct: FinancialProductTracking = {
        productName: '',
        downpaymentValue: 0,
        downpaymentPercentage: 0,
        paymentMonthly: 0,
        financingDuration: 0,
        productType: '',
        cashPrice: 0
    };

    constructor(config: InitialConfiguration) {
        this.config = config;
    }

    /**
     * Method for create and return an object of type EventData with the default
     * configuration from the global GDL-object.
     * @param gdl GDL.
     * @returns {EventData}.
     */
    private getAnEventDataObject = (): EventData => {
        return {
            context: {
                moduleId: this.config.id,
            },
            pageExperience: {
                sectionName: 'Financial Calculator',
            }
        };
    }

    /**
     * Method for push the tracking data to the GDL package, if an error occurred this 
     * will throw an alarm with the error.
     * @param event string.
     * @param financialProduct FinancialProductTracking.
     */
    private track = (event: string, financialProduct: FinancialProductTracking, componentClick?: ComponentClick) => {
        try {
            this.setFinancialProductData(financialProduct);

            if (this.config.brand === PORSCHE_BRAND && window.GDL) {
                
                const eventData: EventData = Object.assign({
                    ...this.getAnEventDataObject(),
                    financialProduct,
                }, componentClick ? { componentClick } : {});

                window.GDL.push(event, { ...eventData });
            } else if (this.config.brand === PORSCHE_BRAND && !window.GDL) {
                /**
                 * This functionality is omitted by the moment, the reason is avoid to
                 * send a lot alarms, the proposal is track this notification in a 
                 * dynamo table in next effort or release.
                 */
                // this.throwAnAlarm(SUBJECT_FOR_ALARM, 'Object GDL not found', TRACK_METHOD_NAME);
            }

            // tracking adobe analytics   
        } catch (error) {
            this.throwAnAlarm(SUBJECT_FOR_ALARM, error.message, TRACK_METHOD_NAME);
        }
    }

    /**
     * Method for throw an alarm when an error occurred.
     * @param subject 
     * @param error 
     * @param method 
     */
    private throwAnAlarm = (subject: string, error: string, method: string) => {
        this.appService.fetchAlarms({
            subject,
            data: {
                error,
                method,
            }
        });
    }

    /**
     * Set a new financial product values.
     * @param financialProduct FinancialProductTracking.
     */
    public setFinancialProductData = (financialProduct: FinancialProductTracking) => {
        this.financialProduct = financialProduct;
    }

    /**
     * Get a financial product object.
     * @returns {FinancialProductTracking}.
     */
    public getFinancialProductData = (): FinancialProductTracking => {
        return this.financialProduct;
    }

    /**
     * Tracking the financial-products loaded when the calculator rendered successfully.
     */
    public loadTheFinancialCalculator = (financialProduct: FinancialProductTracking): void => {
        this.track(TrackingEvents.loadTheFinancialProducts, financialProduct);
    }

    /**
     * Tracking the new financial product selected
     */
    public changeFinancialProducts = (data: FinancialProductTracking, idInput: string) => {
        const componentClick = this.getAComponentClick(idInput, 'compare-and-select-input', 'Radio');
        this.track(TrackingEvents.changeFinancialProducts, data, componentClick);
    }

    /**
     * Tracking the new down payment percentage selected
     */
    public changeDownPaymentPercentage = (
        downpaymentValue: number,
        downpaymentPercentage: string,
        paymentMonthly: number
    ) => {
        const downpaymentPercentageValue = Number(downpaymentPercentage.replace('%', ''));

        const financialProduct = {
            ...this.getFinancialProductData(),
            downpaymentPercentage: downpaymentPercentageValue,
            downpaymentValue,
            paymentMonthly,
        };

        const componentClick = this.getAComponentClick(downpaymentPercentage, 'compare-and-select-percentages', 'Radio');
        const oldDownpaymentPercentage = this.getFinancialProductData().downpaymentPercentage;
        
        if (downpaymentPercentageValue > oldDownpaymentPercentage) {
            this.track(TrackingEvents.increaseDownPaymentPercentage, financialProduct, componentClick);
        } else {
            this.track(TrackingEvents.decreaseDownPaymentPercentage, financialProduct, componentClick);
        }       
    }

    /**
     * Tracking the new duration month selected
     */
    public changeMonthDuration = (paymentMonthly: number, financingDuration: number, idInput: string) => {
        const financialProduct = {
            ...this.getFinancialProductData(),
            paymentMonthly,
            financingDuration,
        };

        const componentClick = this.getAComponentClick(idInput, `compare-and-select-durations`, 'Radio');
        this.track(TrackingEvents.changeMonthDuration, financialProduct, componentClick);
    }

    /**
     * Method to create and return an object type ComponentClick for the
     * tracking data.
     * @param idInput string.
     * @param name string.
     * @param type string.
     * @returns {ComponentClick}.
     */
    private getAComponentClick = (idInput: string, name: string, type: string): ComponentClick => {
        return {
            clickElementType: type,
            clickElementId: idInput,
            clickElementName: name,
            targetUrl: '',
        };
    }

    /**
     * Tracking when the user clicks the continue button
     */
    public openModalToOut = (idButton: string) => {
        const componentClick = this.getAComponentClick(idButton, '', 'Button');
        this.track(TrackingEvents.openModalToOut, this.getFinancialProductData(), componentClick);
    }

    /**
     * Tracking when the user go to the preapproval application	
     */
    public gotoPreApprovalApp = (idButton: string) => {
        const componentClick = this.getAComponentClick(idButton, '', 'Button');
        this.track(TrackingEvents.gotoPreApprovalApp, this.getFinancialProductData(), componentClick);
    }
}

export default TrackingManager;
